import { ReactElement, useCallback } from 'react';
import { Trans } from 'react-i18next';

import {
   MedicalDeviceOrderApproveResult,
   MedicalDeviceOrderStatus,
   MedicalDeviceOrderVerificationRetryCommand,
} from '@dreryk/egabinet-ezwm-frontend-api';
import { HealthcareProviderType } from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { PromiseVoidFunction } from '@dreryk/react-common';
import { dispatchSnackbarInformativeEvent, dispatchSnackbarNegativeEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { protectFeature } from '~libs.shared.system';
import { isNfzMfaLoginFault, useEzwmEnabledChecker, useEzwmMfaGuard } from '~legacy.personnel';

import { MedicalDeviceOrderEzwmAuthenticationFault } from '../../medicalDeviceOrder/MedicalDeviceOrderEzwmLoginErrorsModal';
import { medicalDevicesTranslationKeys } from '../../translations';

import { GoToNFZSettingsLink } from './GoToNfzLink';

const DEVICE_ORDER_ERROR_STATUSES: MedicalDeviceOrderStatus[] = ['INCORRECT', 'REJECTED'];

const dispatchNoNFZConfigurationNegativeSnackbar = (healthcareProviderType: HealthcareProviderType): void => {
   dispatchSnackbarNegativeEvent(
      protectFeature(
         'updatingProviderData',
         <Trans
            i18nKey={medicalDevicesTranslationKeys.nfzConfigurationFailedSnackbarMessageForAdmin}
            components={{
               link: <GoToNFZSettingsLink healthcareProviderType={healthcareProviderType} />,
            }}
         />,
         medicalDevicesTranslationKeys.nfzConfigurationFailedSnackbarMessageForNonAdmin
      )
   );
};

const getMedicalDeviceOrderVerificationRetryCommand = (
   externalId: string,
   mfaTotp?: string
): MedicalDeviceOrderVerificationRetryCommand => {
   const command = new MedicalDeviceOrderVerificationRetryCommand();
   command.externalId = externalId;

   if (mfaTotp) {
      command.mfaTotp = Choice.of(mfaTotp);
   }

   return command;
};

type UseVerifyAgainActionOptions = {
   externalId: string;
   healthcareProviderType: HealthcareProviderType;
   onSuccess: VoidFunction;
   onFault: (fault: MedicalDeviceOrderEzwmAuthenticationFault) => void;
};

type UseVerifyAgainActionReturn = {
   verifyAgain: PromiseVoidFunction;
   renderMfaModal: () => ReactElement | null;
};

export const useVerifyAgainAction = (options: UseVerifyAgainActionOptions): UseVerifyAgainActionReturn => {
   const { externalId, healthcareProviderType, onSuccess, onFault } = options;
   const { checkMfaRequired, openMfaModal, renderMfaModal, mfaModalOpened } = useEzwmMfaGuard({
      blockingOperationLabel: medicalDevicesTranslationKeys.verifyingDocument,
   });
   const ezwmEnabledCheck = useEzwmEnabledChecker();

   const handleFault = useCallback(
      (fault: MedicalDeviceOrderEzwmAuthenticationFault): void => {
         if (mfaModalOpened && isNfzMfaLoginFault(fault)) {
            return;
         }

         onFault(fault);
      },
      [mfaModalOpened, onFault]
   );

   const { executeAsync: retryDocumentVerification } = useCqrsCommand<
      MedicalDeviceOrderApproveResult,
      { mfaTotp?: string },
      MedicalDeviceOrderEzwmAuthenticationFault
   >((parameters) => getMedicalDeviceOrderVerificationRetryCommand(externalId, parameters?.mfaTotp), {
      onSuccess: (result) => {
         onSuccess();
         if (DEVICE_ORDER_ERROR_STATUSES.includes(result.status)) {
            dispatchSnackbarNegativeEvent(medicalDevicesTranslationKeys.orderVerifiedNegatively);
            return;
         }
         if (result.status === 'VERIFICATION') {
            dispatchSnackbarInformativeEvent(medicalDevicesTranslationKeys.orderStillInVerification);
         }
      },
      onFault: {
         'egabinet-ezwm-frontend-api:EzwmAccountLoginFault': handleFault,
         'egabinet-ezwm-frontend-api:EzwmAccountBlockedFault': handleFault,
         'egabinet-ezwm-frontend-api:EzwmPasswordExpiredFault': handleFault,
      },
   });

   const verifyAgain = useCallback(async (): Promise<void> => {
      const ezwmEnabled = await ezwmEnabledCheck();
      if (!ezwmEnabled) {
         dispatchNoNFZConfigurationNegativeSnackbar(healthcareProviderType);
         return;
      }

      const mfaRequired = await checkMfaRequired();
      if (mfaRequired) {
         openMfaModal(async (mfaTotp) => {
            await retryDocumentVerification({ mfaTotp: mfaTotp });
         });
         return;
      }

      await retryDocumentVerification({});
   }, [ezwmEnabledCheck, healthcareProviderType, checkMfaRequired, openMfaModal, retryDocumentVerification]);

   return {
      verifyAgain: verifyAgain,
      renderMfaModal: renderMfaModal,
   };
};
